import { useEffect, useState } from 'react';

import { SupportedUserLanguages, UserLanguages } from 'ev-types';

import { useUpdateUserLocaleMutation } from 'ev-api/core';
import {
  useNullableCurrentPractice,
  useNullablePrimaryUser,
} from 'ev-hooks/commonData';
import { getSupportedLanguagesForPractice } from 'ev-utils/i18n';
import { getCleanedUserLocale } from 'ev-utils/user';

import i18n, { DEFAULT_LOCALE } from './i18n';

function getLanguage(
  currentLanguage: string,
  supportedLanguages: string[],
): SupportedUserLanguages | undefined {
  currentLanguage = getCleanedUserLocale(currentLanguage as UserLanguages);
  if (supportedLanguages.includes(currentLanguage)) {
    return currentLanguage as SupportedUserLanguages;
  }
}

type LanguageUpdaterProps = {
  onUserLocaleUpdated?: (language: string) => void;
};

export function useLanguageUpdater({
  onUserLocaleUpdated,
}: LanguageUpdaterProps = {}) {
  const practice = useNullableCurrentPractice();
  const { primaryUser, fetchingPrimaryUser } = useNullablePrimaryUser();
  const browserLocale = navigator.language;
  const [, setLanguage] = useState('');

  const [updateUserLocale] = useUpdateUserLocaleMutation();
  useEffect(() => {
    const updateLocale = async (lng: string) => {
      if (
        !fetchingPrimaryUser &&
        primaryUser &&
        primaryUser.attributes.locale !== i18n.language
      ) {
        await updateUserLocale({ locale: lng }).unwrap();
      }
      onUserLocaleUpdated?.(lng);
    };
    i18n.on('languageChanged', updateLocale);
    return () => i18n.off('languageChanged', updateLocale);
  }, [primaryUser, onUserLocaleUpdated, updateUserLocale, fetchingPrimaryUser]);

  useEffect(() => {
    const supportedLanguages = getSupportedLanguagesForPractice(practice);
    const userLocale = primaryUser?.attributes.locale || '';
    const supportedUserLocale = getLanguage(userLocale, supportedLanguages);
    const supportedBrowserLocale = getLanguage(
      browserLocale,
      supportedLanguages,
    );
    let locale =
      supportedUserLocale || supportedBrowserLocale || DEFAULT_LOCALE;
    if (locale !== i18n.language) {
      if (primaryUser && !supportedUserLocale && i18n.language) {
        locale = i18n.language;
      }
      setLanguage(locale);
      void i18n.changeLanguage(locale);
    }
  }, [browserLocale, practice, primaryUser, primaryUser?.attributes.locale]);

  return i18n.language;
}
